import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { useI18n, LOCALES, type Locale } from '../../i18n';
import { useAuth } from '../../lib/auth';
import { BottleCap } from './BottleCap';
import { useProfile } from '../../state/profile';
import { Icon } from './icons';

type Tab = { key: string; path: string; auth?: boolean };
const TABS: Tab[] = [
  { key: 'play', path: '/play' },
  { key: 'boards', path: '/boards' },
  { key: 'profile', path: '/profile', auth: true },
  { key: 'info', path: '/info' },
  { key: 'partners', path: '/partners' },
];

/**
 * Global auth-aware header for the web app. Top-nav tabs collapse into a
 * hamburger drawer below 760px. Shows Login when signed out; an avatar menu
 * (Profile / Logout, plus Admin/Partner shortcuts gated by role) when signed in.
 */
export function Header() {
  const { t, locale, setLocale, localePath } = useI18n();
  const { status, user, signOut } = useAuth();
  const { profile } = useProfile();
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close transient UI on navigation.
  useEffect(() => {
    setMenuOpen(false);
    setDrawerOpen(false);
  }, [loc.path]);

  const signedIn = status === 'signed-in';
  const isActive = (path: string) => loc.path === localePath(path) || loc.path.startsWith(localePath(path) + '/');
  const visibleTabs = TABS.filter((tab) => !tab.auth || signedIn);

  const loginHref = `${localePath('/login')}?return=${encodeURIComponent(loc.path)}`;

  function NavLinks({ onNavigate }: { onNavigate?: () => void }) {
    return (
      <>
        {visibleTabs.map((tab) => (
          <a
            key={tab.key}
            href={localePath(tab.path)}
            class={`bc-tab${isActive(tab.path) ? ' is-active' : ''}`}
            onClick={onNavigate}
          >
            {t(`header.tabs.${tab.key}`)}
          </a>
        ))}
      </>
    );
  }

  return (
    <header class="bc-header">
      <div class="bc-header__inner">
        <a class="bc-wordmark" href={signedIn ? localePath('/play') : localePath()}>
          BeerCup<span>·26</span>
        </a>

        <nav class="bc-tabs">
          <NavLinks />
        </nav>

        <div class="bc-header__right">
          <div class="bc-lang">
            {LOCALES.map((l: Locale) => (
              <button
                key={l}
                type="button"
                class={l === locale ? 'is-active' : ''}
                aria-pressed={l === locale}
                onClick={() => setLocale(l)}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>

          {status === 'unknown' ? (
            <span class="bc-header__placeholder" aria-hidden="true" />
          ) : signedIn ? (
            <div class="bc-usermenu">
              <button
                type="button"
                class="bc-avatarbtn"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                {user?.avatarUrl ? (
                  <img class="bc-avatarimg" src={user.avatarUrl} alt="" />
                ) : (
                  <BottleCap code={profile?.avatarCap} size={32} />
                )}
              </button>
              {menuOpen && (
                <>
                  <div class="bc-menu__scrim" onClick={() => setMenuOpen(false)} />
                  <div class="bc-menu" role="menu">
                    <div class="bc-menu__head">
                      <strong>{profile?.nickname ?? user?.displayName ?? t('header.menu.account')}</strong>
                      <span>{user?.email}</span>
                    </div>
                    <a role="menuitem" href={localePath('/profile')}>
                      <Icon.user size={16} /> {t('header.tabs.profile')}
                    </a>
                    {user?.role === 'admin' && (
                      <a role="menuitem" href={localePath('/admin')}>
                        <Icon.settings size={16} /> {t('header.menu.admin')}
                      </a>
                    )}
                    {(user?.role === 'partner' || user?.role === 'admin') && (
                      <a role="menuitem" href={localePath('/partner')}>
                        <Icon.pin size={16} /> {t('header.menu.partner')}
                      </a>
                    )}
                    <button role="menuitem" type="button" onClick={() => signOut()}>
                      <Icon.logout size={16} /> {t('header.menu.logout')}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <a class="bc-login" href={loginHref}>
              {t('header.login')}
            </a>
          )}

          <button
            type="button"
            class="bc-burger"
            aria-label={t('header.menu.nav')}
            onClick={() => setDrawerOpen((v) => !v)}
          >
            {drawerOpen ? <Icon.close /> : <Icon.menu />}
          </button>
        </div>
      </div>

      {drawerOpen && (
        <nav class="bc-drawer">
          <NavLinks onNavigate={() => setDrawerOpen(false)} />
        </nav>
      )}
    </header>
  );
}
